// Public profile preview + subscribe flow. The preview reuses /public/{alias} (no auth);
// subscribe redeems a share token and registers the local subscriber public key so the
// server can wrap the split-key subscriber secret to this device.
import { h, toast, icon, tableScroll } from '../components/dom.js';
import { showShareBackModal } from '../components/share-back-modal.js';
import { renderPublicShell } from '../components/shell.js';
import { api } from '../api/client.js';
import { ensureSubscriberKeypair } from '../crypto/splitkey.js';
import { session } from '../api/session.js';
import type { PublicView } from '../api/types.js';import { downloadVCard, buildVCard } from './vcard.js';
import { qualifierSubline } from './fields-catalog.js';
import { preferredName } from '../lib/preferred-name.js';
// subscribeWithShareBack drives the reciprocal share-back flow: it opens the
// profile-picker modal (Public preselected; dismiss => Public), then performs a
// SINGLE subscribe carrying the resolved share_back_profile_id. Returns the
// resulting subscription so callers can toast pending vs. active.
async function subscribeWithShareBack(alias: string, code: string, inviterName: string) {
  let shareBackProfileId: string | undefined;
  try {
    const profiles = (await api.profiles()).data ?? [];
    if (profiles.length) {
      const res = await showShareBackModal(inviterName, profiles);
      shareBackProfileId = res.profileId;
    }
  } catch {
    // If we can't load profiles or show the modal, fall through: subscribe
    // without a share-back id (server defaults to the subscriber's Public).
  }
  return api.subscribeByCode(alias, code, shareBackProfileId);
}

export async function renderPublic(params: Record<string, string>): Promise<void> {
  const alias = params.alias;
  const view = await api.publicView(alias).catch(() => null);

  const content = renderPublicShell();
  content.append(h('p', { class: 'center muted' }, h('small', {}, `virtualid.one/id/${alias} · public profile`)));
  content.append(view
    ? publicProfileCard(view, alias, { interactive: true })
    : h('div', { class: 'card' }, notFoundPanel(alias)));
  content.append(h('p', { class: 'center', style: 'margin-top:22px' },
    h('small', { html: 'Powered by <a href="/">VirtualID</a> — one identity, one you.' })));
}

// publicProfileCard renders the subscriber-facing profile card — the SAME template
// used on the public /id page and the landing demo. `interactive` toggles the
// Subscribe / Save-vCard actions (owner Preview passes interactive:false since a
// preview shouldn't subscribe you to yourself).
export function publicProfileCard(
  view: PublicView,
  alias: string,
  opts: { interactive?: boolean; showVCard?: boolean } = {},
): HTMLElement {
  const interactive = opts.interactive !== false;
  const showVCard = opts.showVCard !== false;
  const card = h('div', { class: 'card center', style: 'position:relative' });

  const fields = view.fields || [];
  const fieldByType = (t: string) => fields.find((f) => f.type === t);
  const given = view.given_name || fieldByType('given_name')?.value || '';
  const family = view.family_name || fieldByType('family_name')?.value || '';
  const jobTitle = fieldByType('job_title')?.value || '';
  const company = fieldByType('company')?.value || '';

  // Title: prefer given/family name, else USERNAME/handle, display_name last.
  const fullName = `${given} ${family}`.trim();
  const title = preferredName({ fullName, username: (view as any).username, handle: view.display_name || alias }) || alias;

  // Initials for the round avatar.
  const initials = (given || family)
    ? ((given.slice(0, 1) + family.slice(0, 1)).toUpperCase() || title.slice(0, 2).toUpperCase())
    : title.slice(0, 2).toUpperCase();

  // Subtitle: job title · company if present, else the handle.
  const subtitleParts = [jobTitle, company].filter(Boolean);
  const subtitle = subtitleParts.length ? subtitleParts.join(' · ') : `virtualid.one/id/${alias}`;
  // When job/company are shown in the subtitle, don't repeat them in the table.
  const consumedInSubtitle = subtitleParts.length > 0;

  card.append(
    h('div', { style: 'width:88px;height:88px;border-radius:50%;background:var(--brand-soft);color:var(--brand);display:flex;align-items:center;justify-content:center;font-size:2rem;font-weight:720;margin:0 auto 14px' }, initials),
    h('h2', { style: 'margin-bottom:2px' }, title),
    h('p', { class: 'muted', style: 'margin-bottom:14px' }, subtitle),
    h('hr', { class: 'divider' }),
  );

  // Rows to render in the table: exclude given/family always; exclude job/company
  // only when consumed by the subtitle.
  const excluded = new Set<string>(['given_name', 'family_name']);
  if (consumedInSubtitle) { excluded.add('job_title'); excluded.add('company'); }
  const rowFields = fields.filter((f) => !excluded.has(f.type));

  // Links on/off toggle — UI-only local view state. Default = links ON.
  let linksOn = true;
  const tableWrap = h('div', {});

  const renderRows = () => {
    tableWrap.replaceChildren();
    if (!rowFields.length) {
      tableWrap.append(h('p', { class: 'muted', style: 'padding:8px 0' }, 'No fields are exposed on this profile yet.'));
      return;
    }
    const table = h('table', { class: 'table', style: 'text-align:left' });
    for (const f of rowFields) {
      const valueCell = (linksOn && f.url)
        ? h('td', {}, h('a', { href: f.url }, f.value))
        : h('td', {}, f.value);
      const header = f.type_name || f.type;
      const qual = qualifierSubline(f.label);
      const th = qual
        ? h('th', { style: 'width:120px' },
            h('div', {}, header),
            h('small', { class: 'muted', style: 'font-weight:400' }, qual))
        : h('th', { style: 'width:120px' }, header);
      table.append(h('tr', {}, th, valueCell));
    }
    tableWrap.append(tableScroll(table));
  };
  renderRows();

  if (rowFields.some((f) => f.url)) {
    // Icon-only toggle pinned to the card's top-right corner. Link icon = links ON
    // (values are clickable); broken-link icon = links OFF (plain text, safe to copy).
    const LINK_ON = '<path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1"/>';
    const LINK_OFF = '<path d="M9 17H7a5 5 0 0 1 0-10h2"/><path d="M15 7h2a5 5 0 0 1 4 8"/><line x1="3" y1="3" x2="21" y2="21"/>';
    const toggle = h('button', {
      class: 'links-toggle', type: 'button',
      title: 'Toggle links', 'aria-label': 'Toggle links',
      'aria-pressed': 'true',
    }, icon(LINK_ON, 18)) as HTMLButtonElement;
    toggle.addEventListener('click', () => {
      linksOn = !linksOn;
      toggle.setAttribute('aria-pressed', String(linksOn));
      toggle.title = linksOn ? 'Links on — click to show plain text' : 'Links off — click to show links';
      toggle.replaceChildren(icon(linksOn ? LINK_ON : LINK_OFF, 18));
      renderRows();
    });
    toggle.title = 'Links on — click to show plain text';
    card.append(toggle);
  }
  card.append(tableWrap);

  const vcardBtn = h('button', { class: 'btn btn-ghost' }, 'Download vCard');
  vcardBtn.addEventListener('click', () => {
    const vcf = buildVCard({
      fn: title,
      fields: (fields || []).map((f) => ({ type: String(f.type), value: f.value, annotation: f.label })),
    });
    downloadVCard(`${alias}.vcf`, vcf);
  });

  if (interactive) {
    const subscribeBtn = h('button', { class: 'btn btn-primary' }, 'Accept Invite & Subscribe');
    subscribeBtn.addEventListener('click', () => {
      if (!session.isLoggedIn) { location.hash = '/login'; return; }
      toast('Ask ' + title + ' for a share link to subscribe and stay in sync.', 'warn');
    });
    card.append(h('div', { class: 'row', style: 'justify-content:center;margin-top:20px' }, subscribeBtn, vcardBtn));
    card.append(h('p', { class: 'muted', style: 'margin-top:14px' }, h('small', {},
      'Subscribing shares your subscriber public key so future updates are delivered end-to-end (split-key). Your private key never leaves this device.')));
  } else if (showVCard) {
    card.append(h('div', { class: 'row', style: 'justify-content:center;margin-top:20px' }, vcardBtn));
  }
  return card;
}

// Friendly empty-state shown for BOTH truly-missing and disabled public profiles
// (uniform 404 — no distinction leaked). Inline SVG illustration, no new assets.
function notFoundPanel(alias: string): HTMLElement {
  const svg = `
<svg width="180" height="140" viewBox="0 0 180 140" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="No profile found illustration">
  <rect x="30" y="28" width="120" height="84" rx="12" fill="var(--brand-soft)" stroke="var(--border)" stroke-width="2"/>
  <circle cx="90" cy="60" r="18" fill="#fff" stroke="var(--border)" stroke-width="2"/>
  <path d="M74 92c0-9 7.2-16 16-16s16 7 16 16" fill="#fff" stroke="var(--border)" stroke-width="2"/>
  <path d="M118 34l30 30M148 34l-30 30" stroke="var(--brand)" stroke-width="3" stroke-linecap="round"/>
  <circle cx="133" cy="49" r="22" fill="none" stroke="var(--brand)" stroke-width="3"/>
</svg>`;
  const home = h('a', { class: 'btn btn-ghost btn-sm', href: '#/' }, 'Go home');
  const signup = h('a', { class: 'btn btn-primary btn-sm', href: '#/register' }, 'Create your VirtualID');
  return h('div', { style: 'text-align:center;padding:12px 4px' },
    h('div', { style: 'margin:0 auto 14px', html: svg }),
    h('h2', { style: 'margin:0 0 6px' }, 'No profile here'),
    h('p', { class: 'muted', style: 'margin:0 0 4px' }, h('span', {}, 'There’s no VirtualID profile at ', h('strong', {}, alias), '.')),
    h('p', { class: 'muted', style: 'margin:0 0 18px' }, h('small', {}, 'It may never have existed, or the owner has disabled it.')),
    h('div', { class: 'row', style: 'justify-content:center;gap:8px' }, signup, home));
}

// Redeem an inbound share LINK → subscribe. Routes:
//   #/subscribe?token=...           (legacy opaque token, back-compat)
//   #/id/:alias/subscribe?code=...  (owner-scoped alias + code; code may come via ?token=/?code=)
export async function renderSubscribe(p: Record<string, string>, query: URLSearchParams): Promise<void> {
  const main = renderPublicShell();
  const alias = p.alias || '';
  const code = query.get('code') || '';
  const token = query.get('token') || (!alias ? query.get('code') || '' : '');
  const haveInvite = !!(alias && (code || token));
  main.append(h('div', { class: 'card', style: 'max-width:480px' },
    h('h2', {}, 'Subscribe'),
    (token || haveInvite) ? h('p', {}, 'Redeeming your invitation…') : h('p', { class: 'muted' }, 'No share token supplied.')));

  if (!token && !haveInvite) return;
  try {
    if (!session.isLoggedIn) {
      try { sessionStorage.setItem('vid_pending_invite', JSON.stringify(alias ? { alias, code: code || token } : { token })); } catch { /* ignore */ }
      toast('Sign in first to subscribe.', 'warn'); location.hash = '/login'; return;
    }
    await ensureSubscriberKeypair().catch(() => undefined);
    let pending = false;
    if (alias && (code || token)) {
      const s = await subscribeWithShareBack(alias, code || token, alias);
      pending = s.status === 'pending';
    } else {
      await api.subscribe(token);
    }
    if (pending) {
      toast('Request sent — awaiting the owner’s approval.', 'ok');
    } else {
      toast('Subscribed! Added to My Contacts.', 'ok');
    }
    setTimeout(() => (location.hash = '/contacts'), 1200);
  } catch (e) {
    toast((e as Error).message, 'danger');
  }
}

// Redeem a CODED invite (owner-scoped) → subscribe. Route: #/id/:alias/invite/:code
// (reached from the PATH deep-link /id/<alias>/invite/<code> via the boot translator).
// Shows the profile preview + invite context, then Subscribe / Download vCard. Redeeming
// requires the subscriber to be logged in; if not, we stash the target and bounce to login.
export async function renderInvite(params: Record<string, string>, query?: URLSearchParams): Promise<void> {
  const alias = params.alias;
  const code = params.code;
  const wrap = renderPublicShell();

  const view = await api.resolveInvite(alias, code).catch(() => null);
  if (!view) {
    wrap.append(h('div', { class: 'card' },
      h('span', { class: 'eyebrow' }, 'Profile-specific invitation'),
      h('h2', { style: 'margin:.3rem 0 .2rem' }, 'Invite not found'),
      h('p', { class: 'muted' }, `We couldn’t find a valid invite at /id/${alias}/invite/${code}. It may have been revoked, expired, or fully used.`),
      h('a', { class: 'btn btn-ghost btn-sm', href: `#/id/${alias}`, style: 'margin-top:6px' }, `View ${alias}’s public profile →`)));
    return;
  }

  const name = preferredName({ username: (view as any).username, handle: view.display_name, display_name: view.profile?.name }) || alias;

  // Header: eyebrow + headline, matching the marketing demo tone.
  wrap.append(h('div', { class: 'center', style: 'margin-bottom:18px' },
    h('span', { class: 'eyebrow' }, 'Profile-specific invitation'),
    h('h1', { style: 'font-size:clamp(1.5rem,3.5vw,2rem);margin:.35rem 0 0' },
      `You’ve been invited to ${name}’s profile`)));

  // Shared polished profile card (same one used by public + preview). interactive:false
  // because the invite page renders its own Subscribe-with-code + vCard actions below.
  wrap.append(publicProfileCard(view as unknown as PublicView, alias, { interactive: false, showVCard: false }));

  // Primary actions live RIGHT UNDER the profile card (above the invite-meta),
  // stacked vertically & centered: a large prominent "Accept Invite & Subscribe"
  // with "Download vCard" (its normal small size) below it — not beside it.
  const actions = h('div', {
    class: 'center',
    style: 'display:flex;flex-direction:column;align-items:center;gap:10px;margin:18px auto 4px;max-width:420px',
  });
  wrap.append(actions);

  // Compact invite meta below the card (code, profile, status) — muted, centered.
  const metaRows: HTMLElement[] = [];
  const tr = (k: string, v: Node | string) => h('tr', {}, h('th', { style: 'width:140px;text-align:left' }, k), h('td', {}, v));
  metaRows.push(tr('Invitation code', h('code', {}, view.code || code)));
  if (view.label) metaRows.push(tr('Issued to', view.label));
  metaRows.push(tr('Status', h('span', { class: `badge ${view.valid ? 'badge-ok' : 'badge-warn'}` }, view.valid ? 'Active' : view.validity)));
  wrap.append(h('div', { class: 'card', style: 'max-width:420px;margin:16px auto 0' },
    tableScroll(h('table', { class: 'table table-narrow', style: 'text-align:left;width:100%' }, ...metaRows))));

  // Expired / revoked / maxed: stop here with a clear notice.
  if (!view.valid) {
    const hasPublic = (view.fields || []).length > 0 || !!view.profile;
    wrap.append(h('div', { class: 'center', style: 'margin-top:14px' },
      h('p', { style: 'margin:0' }, h('span', { class: 'badge badge-warn' }, view.validity),
        ' ', h('span', { class: 'muted' }, 'This invite is no longer redeemable.')),
      h('p', { class: 'muted', style: 'margin:6px 0 0;font-size:.85rem' },
        hasPublic
          ? 'The invitation’s private details are hidden. Only this person’s public information is shown above.'
          : 'Only this person’s username is shown — they have no public information available.'),
      h('a', { class: 'btn btn-ghost btn-sm', href: `#/id/${alias}`, style: 'margin-top:10px' }, `View ${alias}’s public profile →`)));
    return;
  }

  const subscribeBtn = h('button', {
    class: 'btn btn-primary btn-lg',
    style: 'font-size:1.05rem;font-weight:650;padding:14px 28px',
  }, 'Accept Invite & Subscribe');

  // Shared subscribe action, used by both the button click and the automatic
  // post-login resume (so the user never has to click Subscribe twice).
  const doSubscribe = async (): Promise<void> => {
    if (subscribeBtn.hasAttribute('disabled')) return;
    subscribeBtn.setAttribute('disabled', 'true');
    try {
      await ensureSubscriberKeypair().catch(() => undefined);
      const sub = await subscribeWithShareBack(alias, code, name);
      toast(sub.status === 'pending'
        ? 'Request sent — awaiting the owner’s approval.'
        : 'Subscribed! Added to My Contacts.', 'ok');
      setTimeout(() => (location.hash = '/contacts'), 1200);
    } catch (e) {
      toast((e as Error).message, 'danger');
      subscribeBtn.removeAttribute('disabled');
    }
  };

  subscribeBtn.addEventListener('click', () => {
    if (!session.isLoggedIn) {
      try { sessionStorage.setItem('vid_pending_invite', JSON.stringify({ alias, code })); } catch { /* ignore */ }
      location.hash = '/login';
      return;
    }
    void doSubscribe();
  });
  actions.append(subscribeBtn);

  const vcardBtn = h('button', { class: 'btn btn-ghost btn-sm' }, 'Download vCard');
  vcardBtn.addEventListener('click', () => {
    const vcf = buildVCard({
      fn: name,
      fields: (view.fields || []).map((f) => ({ type: String(f.type), value: f.value, annotation: f.label })),
    });
    downloadVCard(`${alias}.vcf`, vcf);
  });
  actions.append(vcardBtn);

  // Encryption/sync reassurance footnote stays at the BOTTOM of the page
  // (below the invite-meta card), not tucked under the action buttons.
  wrap.append(h('p', { class: 'center', style: 'margin:16px auto 0;max-width:520px' }, h('small', { class: 'muted' },
    'Subscriptions stay end-to-end encrypted (split-key), stay in sync, and can be revoked by either side. Your private key never leaves this device.')));

  // Auto-resume: if we arrived here right after logging in via the Subscribe
  // bounce (?autosubscribe=1), proceed with the subscribe automatically so the
  // user doesn't have to click Subscribe a second time.
  if (session.isLoggedIn && query?.get('autosubscribe') === '1') {
    void doSubscribe();
  }
}
